import { useUserSession } from '#imports'
import { clearPersistedFilters } from './usePersistedFilter'

// localStorage keys we explicitly clear on logout. Persistence stays
// across sessions for non-sensitive UI prefs (theme), but per-user state
// (filters, intro overlay flag, onboard prefill) MUST be wiped so the
// next person on the browser doesn't inherit them.
const CLEAR_ON_LOGOUT_PREFIXES = [
    'dz:chat:',
    'diztree:artist-onboard-prefill',
    'diztree:intro-overlay-seen',
    'todo-collab-filter:'
]

function purgeLogoutKeys() {
    if (typeof window === 'undefined') return
    try {
        clearPersistedFilters()
        for (const prefix of CLEAR_ON_LOGOUT_PREFIXES) {
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const k = localStorage.key(i)
                if (k && k.startsWith(prefix)) localStorage.removeItem(k)
            }
        }
        sessionStorage.clear()
    } catch {
        // Quota / privacy mode — non-fatal. The page reload below still happens.
    }
}

// Per-user cookies that must NOT survive a logout on a shared browser, or the
// next person inherits User A's state. Both are written with `Domain=.{active
// domain}` (see useCart / useGenrePreferences), so a plain host-only delete
// silently misses them — the delete must repeat the exact Domain attribute.
const SESSION_SCOPED_COOKIES = ['cart', 'dz_genres']

function clearSessionScopedCookies(activeDomain: string | null | undefined) {
    if (typeof document === 'undefined') return
    const isHostNumericOrLocal = !activeDomain
        || /^(\d{1,3}\.){3}\d{1,3}$/.test(activeDomain)
        || activeDomain === 'localhost'
    const cookieDomain = !isHostNumericOrLocal ? `.${activeDomain}` : null
    for (const name of SESSION_SCOPED_COOKIES) {
        try {
            // Host-only variant (dev / IP hosts).
            document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`
            // Tenant-domain variant — the one the cookie was actually set with.
            if (cookieDomain) {
                document.cookie = `${name}=; Path=/; Domain=${cookieDomain}; Max-Age=0; SameSite=Lax`
            }
        } catch {
            // Cookie scope errors are non-fatal; the post-logout reload
            // re-creates empty state anyway.
        }
    }
}

export const useAuth = () => {
    const { loggedIn, user, session, fetch, clear } = useUserSession()

    const login = async (email: string, password: string) => {
        try {
            const res: any = await $fetch('/api/auth/login', {
                method: 'POST',
                body: { email, password }
            })
            // Server returns `{ twoFactorRequired: true, challengeId, ... }`
            // when the account has 2FA enabled. The session has NOT been
            // set yet — surface the challenge so the caller can render
            // the code-entry step. The composable does NOT call fetch()
            // here because there's no session to load.
            if (res?.twoFactorRequired) {
                return {
                    success: false,
                    twoFactorRequired: true,
                    challengeId: res.challengeId,
                    expiresAt: res.expiresAt,
                    method: res.method,
                    emailHint: res.emailHint
                }
            }
            await fetch()
            return { success: true }
        } catch (error: any) {
            return { success: false, error: error.data?.statusMessage || error.statusMessage || 'Login failed' }
        }
    }

    const verifyTwoFactor = async (challengeId: string, code: string) => {
        try {
            const res: any = await $fetch('/api/auth/login-2fa', {
                method: 'POST',
                body: { challengeId, code }
            })
            await fetch()
            return {
                success: true,
                // Populated when the user arrived at the 2FA prompt via the
                // OAuth bridge — the bridge stashed its `next` parameter in
                // a signed cookie that the server has now consumed and
                // forwarded back. Falsy for password logins, where the
                // caller falls back to role-based defaults.
                redirectUrl: typeof res?.redirectUrl === 'string' ? res.redirectUrl : null
            }
        } catch (error: any) {
            return {
                success: false,
                reason: error.data?.data?.reason || null,
                attemptsLeft: error.data?.data?.attemptsLeft,
                error: error.data?.statusMessage || error.statusMessage || 'Verification failed'
            }
        }
    }

    const resendTwoFactor = async (challengeId: string) => {
        try {
            const res: any = await $fetch('/api/auth/login-2fa', {
                method: 'POST',
                body: { challengeId, resend: true }
            })
            return {
                success: true,
                challengeId: res.challengeId,
                expiresAt: res.expiresAt,
                cooldownActive: res.cooldownActive === true
            }
        } catch (error: any) {
            return {
                success: false,
                error: error.data?.statusMessage || error.statusMessage || 'Could not resend code'
            }
        }
    }

    const register = async (name: string, email: string, password: string, role = 'customer', termsAccepted = false) => {
        try {
            await $fetch('/api/auth/register', {
                method: 'POST',
                body: { name, email, password, role, termsAccepted }
            })
            await fetch()
            return { success: true }
        } catch (error: any) {
            return { success: false, error: error.data?.statusMessage || error.statusMessage || 'Registration failed' }
        }
    }

    const logout = async () => {
        try {
            await clear()
            purgeLogoutKeys()
            // Clear per-user cookies (cart, genre taste profile) at the session
            // boundary — on a shared browser they must not carry into the next
            // person's session. Done here (not in useCart/useGenrePreferences)
            // because logout is the only moment we can reliably call it a
            // boundary; every other write to these is intentional.
            clearSessionScopedCookies((useState('tenant') as any)?.value?.activeDomain)
            // Sign out where you signed in. Sending a storefront customer to
            // the tenant's marketing homepage dropped them out of the shop
            // entirely — a relative path keeps them on shop.{tenant} without
            // any protocol/port guessing, and the shop's own login is the
            // page that actually gets them back in.
            const host = typeof window !== 'undefined' ? window.location.hostname : ''
            if (host.startsWith('shop.')) {
                window.location.href = '/login'
            } else {
                window.location.href = getMainDomainUrl('/')
            }
        } catch (error) {
            console.error(error)
        }
    }

    return {
        user,
        loggedIn,
        login,
        verifyTwoFactor,
        resendTwoFactor,
        register,
        logout,
        fetch
    }
}
